import {
  ApolloError,
  DocumentNode,
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  TypedDocumentNode,
  useQuery
} from '@apollo/client'

// When the error policy is 'none', apollo will either populate the "data" or "error",
// depending on whether any graphql errors were returned. This union represents that
// a result may either be data OR error.
// We omit the data and error fields from the apollo type and intersect them with
// their respective non-nullable fields for each of our union members.
type Loading<TData, TVariables extends OperationVariables> = Omit<
  QueryResult<TData, TVariables>,
  'data' | 'error'
>

type Ok<TData, TVariables extends OperationVariables> = Omit<
  QueryResult<TData, TVariables>,
  'data' | 'error' | 'loading'
> & { data: TData }

type Error<TData, TVariables extends OperationVariables> = Omit<
  QueryResult<TData, TVariables>,
  'data' | 'error' | 'loading'
> & {
  error: Omit<ApolloError, 'networkError'>
}

type Result<TData, TVariables extends OperationVariables> =
  | Ok<TData, TVariables>
  | Error<TData, TVariables>

export function isLoading<TData, TVariables extends OperationVariables>(
  res: Loading<TData, TVariables> | Result<TData, TVariables>
): res is Loading<TData, TVariables> {
  return 'loading' in res && res.loading
}

export function isErr<TData, TVariables extends OperationVariables>(
  res: Result<TData, TVariables>
): res is Error<TData, TVariables> {
  return 'error' in res
}

export default function useApolloQuery<
  TData,
  TVariables extends OperationVariables = OperationVariables
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  // Options shadows the apollo options but omits their error policy, which is always set to 'none'
  options?: Omit<QueryHookOptions<TData, TVariables>, 'errorPolicy'>
): Loading<TData, TVariables> | Result<TData, TVariables> {
  const queryResult = useQuery(query, { ...options, errorPolicy: 'none' })

  // If we encounter a network error throw up to error boundary
  // This is also why "networkError" can be omitted from both
  // our result types (we always throw if it's present)
  if (queryResult.error?.networkError) {
    throw queryResult.error.networkError
  }

  if ('loading' in queryResult && queryResult.loading) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { data: _, error: __, ...loadingResult } = queryResult
    return loadingResult
  }

  if ('error' in queryResult) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { data: _, ...errorResult } = queryResult
    if (!errorResult.error) {
      // This should never happen
      throw new Error('Invalid state')
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { networkError: __, ...error } = errorResult.error
    return { ...errorResult, ...{ error } }
  }

  if ('data' in queryResult) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { error: _, ...dataResult } = queryResult
    if (!dataResult.data) {
      // This should never happen
      throw new Error('Invalid state')
    }
    const data = dataResult.data
    return { ...dataResult, ...{ data } }
  }

  // This should never happen
  throw new Error('Invalid state')
}
